import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Text, Box } from 'rebass/styled-components';
import { useTheme } from 'styled-components';
import { RefreshCcw, Zap, AlertTriangle, Pause } from 'lucide-react';

// Single source of truth for status appearance and copy
// Colors reference rombo theme tokens where available
const STATUS_CONFIG = {
  syncing: {
    label: 'Syncing',
    bgColor: 'blue.50',
    iconColor: 'blue.600',
    Icon: RefreshCcw,
  },
  active: {
    label: 'In sync',
    bgColor: 'green.20',
    iconColor: 'green.600',
    Icon: Zap,
  },
  in_sync: {
    label: 'In sync',
    bgColor: 'green.20',
    iconColor: 'green.600',
    Icon: Zap,
  },
  permissions_missing: {
    label: 'Permissions missing',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
  paused: {
    label: 'Paused',
    bgColor: 'neutral.20',
    iconColor: 'neutral.400',
    Icon: Pause,
  },
  reconnection_needed: {
    label: 'Reconnection needed',
    bgColor: 'orange.50',
    iconColor: 'orange.600',
    Icon: AlertTriangle,
  },
};

export const IntegrationStatus = ({ status }) => {
  const { colors } = useTheme();
  const { label, bgColor, iconColor, Icon } = STATUS_CONFIG[status] || STATUS_CONFIG.in_sync;

  // Resolve icon color from theme (e.g., 'green.600' -> '#24A84E')
  const [colorKey, shade] = iconColor.split('.');
  const resolvedIconColor = colors[colorKey]?.[shade] || iconColor;

  return (
    <Flex
      as="span"
      bg={bgColor}
      color="text.dark"
      sx={{
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        px: '12px',
        py: '0px',
        borderRadius: '99px',
        border: '1px solid',
        borderColor: 'neutral.100',
      }}
    >
      <Box as={Icon} width="16px" height="16px" color={resolvedIconColor} aria-hidden="true" />
      <Text as="span" fontSize="14px" fontWeight="500" sx={{ lineHeight: '24px' }}>
        {label}
      </Text>
    </Flex>
  );
};

IntegrationStatus.propTypes = {
  status: PropTypes.oneOf(['syncing', 'in_sync', 'active', 'permissions_missing', 'paused', 'reconnection_needed'])
    .isRequired,
};

export default IntegrationStatus;
